import type { PlannerMode, PlannerValue, TruckPreference } from "./api";

export const MIN_VISIT_MAX = 365;
export const INSPECT_MAX = 100;
export const TEMPLATE_MIN = 7;
export const TEMPLATE_MAX = 180;

export interface PlannerForm {
  minVisit: string;
  inspect: string;
  mode: PlannerMode;
  truckPreference: TruckPreference;
  templateDays: string;
}

export const MODE_NAME: Record<PlannerMode, string> = { auto: "full auto", areas: "areas" };
export const MODE_TEXT: Record<PlannerMode, string> = {
  auto: "Full auto",
  areas: "Areas",
};
export const PREFERENCE_TEXT: Record<TruckPreference, string> = {
  hard: "Hard",
  soft: "Soft",
};

export const plannerForm = (value: PlannerValue): PlannerForm => ({
  minVisit: String(value.min_visit_days),
  inspect: String(Math.round(value.inspect_weight * 1000) / 10),
  mode: value.mode,
  truckPreference: value.truck_preference,
  templateDays: String(value.template_days),
});

/** Read the planner form; give the error and its field, or the value to save. */
export function plannerChange(form: PlannerForm): PlannerValue | { key: keyof PlannerForm; text: string } {
  const cleanDays = form.minVisit.trim();
  const days = Number(cleanDays);
  if (cleanDays === "" || !Number.isInteger(days) || days < 0 || days > MIN_VISIT_MAX)
    return { key: "minVisit", text: `The period must be a whole number of days from 0 to ${MIN_VISIT_MAX}.` };
  const clean = form.inspect.trim().replace(/\s*%$/, "");
  const share = Number(clean);
  if (clean === "" || !Number.isFinite(share) || share < 0 || share > INSPECT_MAX)
    return { key: "inspect", text: `The weight must be from 0% to ${INSPECT_MAX}%.` };
  const cleanTemplate = form.templateDays.trim();
  const template = Number(cleanTemplate);
  if (cleanTemplate === "" || !Number.isInteger(template) || (template !== 0 && (template < TEMPLATE_MIN || template > TEMPLATE_MAX)))
    return { key: "templateDays", text: `The window must be 0 (off) or a whole number of days from ${TEMPLATE_MIN} to ${TEMPLATE_MAX}.` };
  return {
    min_visit_days: days,
    inspect_weight: Math.round(share * 10) / 1000,
    mode: form.mode,
    truck_preference: form.truckPreference,
    template_days: template,
  };
}

/** The default of the minimum visit period, for example "56 days" or "off". */
export const minVisitText = (days: number) => (days > 0 ? `${days} days` : "off");

/** The default of the template window, for example "90 days" or "0 (off)". */
export const templateDaysText = (days: number) => (days > 0 ? `${days} days` : "0 (off)");
